import { AlertCircle, ChevronDown, ChevronRight, FileText, Globe, Server } from 'lucide-react';
import { useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { RunLanguage } from './api';
import { TermsInput } from './TermsInput';

const ACCEPT = '.pdf,.docx,.xlsx,.pptx,.html,.htm,.md,.markdown,.txt,.csv';
const LANGUAGES: readonly RunLanguage[] = ['auto', 'it', 'en'];

export const megabytes = (bytes: number): string =>
  (bytes / (1024 * 1024)).toLocaleString('it-IT', { maximumFractionDigits: 1 });

/** «12 KB» below a tenth of a megabyte, «1,4 MB» above (uiux's review of #572). */
const fileSize = (bytes: number): string =>
  bytes < 0.1 * 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('it-IT')} KB`
    : `${megabytes(bytes)} MB`;

export function FieldError({ message }: { readonly message: string }) {
  return (
    <p role="alert" className="flex items-start gap-2 text-sm text-destructive">
      <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      {message}
    </p>
  );
}

type Tab = 'file' | 'url';

function InputTabs({ tab, onTab }: { readonly tab: Tab; readonly onTab: (id: Tab) => void }) {
  const { t } = useTranslation('tools');
  return (
    <div role="tablist" aria-label={t('cleanText.input.tabsLabel')} className="flex gap-1.5">
      {(['file', 'url'] as const).map((id) => {
        const Icon = id === 'file' ? FileText : Globe;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => onTab(id)}
            className={cn(
              'inline-flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
              tab === id
                ? 'bg-selected text-selected-foreground'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <Icon aria-hidden="true" className="size-4" />
            {t(`cleanText.input.tabs.${id}`)}
          </button>
        );
      })}
    </div>
  );
}

function ErrorBanner({
  message,
  retry,
  onRetry,
}: {
  readonly message: string;
  readonly retry: boolean;
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation('tools');
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-2 rounded-md bg-[var(--error-tint)] p-3 text-sm text-destructive"
    >
      <AlertCircle aria-hidden="true" className="size-4 shrink-0" />
      <span className="grow">{message}</span>
      {retry && (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          {t('cleanText.errors.retry')}
        </Button>
      )}
    </div>
  );
}

interface FileSlotProps {
  readonly file: File | null;
  readonly error: string | null;
  readonly buttonLabel: string;
  readonly onSubmit: () => void;
  readonly onPick: () => void;
}

function FileSlot({ file, error, buttonLabel, onSubmit, onPick }: FileSlotProps) {
  const { t } = useTranslation('tools');
  return (
    <div className="space-y-2">
      <div
        className={cn(
          'flex min-h-44 flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-5 text-center',
          error ? 'border-destructive' : 'border-border',
        )}
      >
        {file ? (
          <>
            <p className="flex items-center gap-2 text-sm">
              <FileText aria-hidden="true" className="size-4.5" />
              <b>{file.name}</b>
              <span className="text-muted-foreground">· {fileSize(file.size)}</span>
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button type="button" onClick={onSubmit}>
                {buttonLabel}
              </Button>
              <Button type="button" variant="ghost" onClick={onPick}>
                {t('cleanText.input.change')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('cleanText.input.dropText')}</p>
            <Button type="button" onClick={onPick}>
              {t('cleanText.input.choose')}
            </Button>
            <p className="text-xs text-muted-foreground">{t('cleanText.input.formats')}</p>
          </>
        )}
      </div>
      {error && <FieldError message={error} />}
    </div>
  );
}

interface UrlSlotProps {
  readonly url: string;
  readonly error: string | null;
  readonly buttonLabel: string;
  readonly onUrl: (value: string) => void;
  readonly onSubmit: () => void;
}

function UrlSlot({ url, error, buttonLabel, onUrl, onSubmit }: UrlSlotProps) {
  const { t } = useTranslation('tools');
  return (
    <div className="space-y-2">
      <label htmlFor="clean-text-url" className="text-sm font-semibold">
        {t('cleanText.input.urlLabel')}
      </label>
      <input
        id="clean-text-url"
        type="url"
        inputMode="url"
        autoComplete="off"
        value={url}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'clean-text-url-error' : undefined}
        placeholder="https://"
        onChange={(e) => onUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
        }}
        className={cn(
          'h-11 w-full rounded-md border bg-card px-3 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          error ? 'border-destructive' : 'border-[var(--control-border)]',
        )}
      />
      {error && (
        <div id="clean-text-url-error">
          <FieldError message={error} />
        </div>
      )}
      <p className="text-sm text-muted-foreground">{t('cleanText.input.urlHint')}</p>
      <Button type="button" onClick={onSubmit}>
        {buttonLabel}
      </Button>
    </div>
  );
}

function LanguagePicker({
  language,
  onLanguage,
}: {
  readonly language: RunLanguage;
  readonly onLanguage: (id: RunLanguage) => void;
}) {
  const { t } = useTranslation('tools');
  return (
    <div className="space-y-1.5">
      <span id="clean-text-language" className="text-sm font-semibold">
        {t('cleanText.options.language')}
      </span>
      <div
        role="radiogroup"
        aria-labelledby="clean-text-language"
        className="flex w-fit rounded-md border border-chip-border p-0.5"
      >
        {LANGUAGES.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={language === id}
            onClick={() => onLanguage(id)}
            className={cn(
              'rounded px-3 py-1.5 text-sm font-semibold',
              language === id ? 'bg-selected text-selected-foreground' : 'text-muted-foreground',
            )}
          >
            {t(`cleanText.options.languages.${id}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface OptionsState {
  readonly language: RunLanguage;
  readonly extraTerms: readonly string[];
  readonly keepTerms: readonly string[];
  readonly open: boolean;
  readonly termsInput: RefObject<HTMLInputElement | null>;
  readonly onLanguage: (id: RunLanguage) => void;
  readonly onExtraTerms: (terms: readonly string[]) => void;
  readonly onKeepTerms: (terms: readonly string[]) => void;
  readonly onToggle: () => void;
}

function OptionsPanel(o: OptionsState) {
  const { t } = useTranslation('tools');
  const summary = [
    t(`cleanText.options.summaryLanguage.${o.language}`),
    t('cleanText.options.summaryExtra', { count: o.extraTerms.length }),
    ...(o.keepTerms.length > 0
      ? [t('cleanText.options.summaryKeep', { count: o.keepTerms.length })]
      : []),
  ].join(' · ');
  const Chevron = o.open ? ChevronDown : ChevronRight;
  return (
    <div className="space-y-3 border-t border-border pt-3">
      <button
        type="button"
        aria-expanded={o.open}
        onClick={o.onToggle}
        className="flex w-full items-center gap-1.5 rounded-md py-1 text-left text-sm font-semibold focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <Chevron aria-hidden="true" className="size-4" />
        {t('cleanText.options.title')}
        {!o.open && <span className="font-normal text-muted-foreground">· {summary}</span>}
      </button>
      {o.open && (
        <div className="space-y-4">
          <LanguagePicker language={o.language} onLanguage={o.onLanguage} />
          <TermsInput
            label={t('cleanText.options.extra')}
            hint={t('cleanText.options.extraHint')}
            placeholder={t('cleanText.options.extraPlaceholder')}
            terms={o.extraTerms}
            onChange={o.onExtraTerms}
            inputRef={o.termsInput}
          />
          <TermsInput
            label={t('cleanText.options.keep')}
            hint={t('cleanText.options.keepHint')}
            placeholder={t('cleanText.options.keepPlaceholder')}
            terms={o.keepTerms}
            onChange={o.onKeepTerms}
          />
        </div>
      )}
    </div>
  );
}

export interface CleanTextInputProps {
  readonly tab: Tab;
  readonly file: File | null;
  readonly url: string;
  readonly fileError: string | null;
  readonly urlError: string | null;
  readonly banner: { readonly message: string; readonly retry: boolean } | null;
  readonly buttonLabel: string;
  readonly options: OptionsState;
  readonly onTab: (id: Tab) => void;
  readonly onUrl: (value: string) => void;
  readonly onChoose: (picked: File | undefined) => void;
  readonly onSubmit: () => void;
  readonly onDrop: (event: React.DragEvent<HTMLElement>) => void;
}

/** The input card: source tabs, error banner, file or address slot, privacy line, options. */
export function CleanTextInput(p: CleanTextInputProps) {
  const { t } = useTranslation('tools');
  const fileInput = useRef<HTMLInputElement | null>(null);
  const pick = (): void => fileInput.current?.click();
  return (
    <section
      className="space-y-4 rounded-xl border border-border bg-card p-4 sm:p-5"
      onDragOver={(e) => e.preventDefault()}
      onDrop={p.onDrop}
    >
      <InputTabs tab={p.tab} onTab={p.onTab} />
      {p.banner && (
        <ErrorBanner message={p.banner.message} retry={p.banner.retry} onRetry={p.onSubmit} />
      )}
      <input
        ref={fileInput}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          p.onChoose(picked);
        }}
      />
      {p.tab === 'file' ? (
        <FileSlot
          file={p.file}
          error={p.fileError}
          buttonLabel={p.buttonLabel}
          onSubmit={p.onSubmit}
          onPick={pick}
        />
      ) : (
        <UrlSlot
          url={p.url}
          error={p.urlError}
          buttonLabel={p.buttonLabel}
          onUrl={p.onUrl}
          onSubmit={p.onSubmit}
        />
      )}
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Server aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span>
          {p.tab === 'url' ? t('cleanText.input.privacyUrl') : t('cleanText.input.privacyFile')}{' '}
          <b>{t('cleanText.input.privacyNotKept')}</b>
        </span>
      </p>
      <OptionsPanel {...p.options} />
    </section>
  );
}
